import { useEffect, useState } from 'react';
import { useT } from '../i18n/locale-context';
import { OTP_RATE_LIMIT_KEY, otpRateLimitSec, otpWaitMinutes } from '../lib/auth-error';

// TZ 2026-08-28 §1: 100% of people who re-requested a code (237/237) confirmed.
// So the resend is the single strongest lever on the 26% drop — it must read as
// a rescue, not hide behind a timer in muted text. ~60s so a slow-but-arriving
// email lands first (the median 1st→2nd request gap was 9 min), then it turns
// into a prominent, one-tap offer with an explicit «check Spam».
const DEFAULT_COOLDOWN_S = 60;

/**
 * «Не пришло письмо? → Отправить код ещё раз» for the email-OTP wait screens
 * (register + login). While the cooldown ticks it stays a quiet line WITH the
 * spam hint already visible (the #1 reason mail "doesn't arrive"); once it
 * expires it becomes a bordered call-to-action, because a resend is the rescue,
 * not a footnote.
 *
 * ## When the server refused to send (22.10)
 *
 * The API caps codes at 5 per address per 15 minutes and, past that, sends
 * nothing. A «ещё раз через 55 с» timer then counts down to a button that is
 * refused again — while the person waits for a letter that is not coming. So
 * while a refusal is in force this renders the plain truth instead of the
 * timer: too many requests, wait N minutes, or use the link in the last letter
 * (which still works — the refused request consumed nothing). When the window
 * opens, the button comes back already active.
 *
 * The refusal arrives two ways: `blockedUntil` from the parent (the `/auth/start`
 * answer said `codeSent: false` before this mounted), or a resend press that
 * the API answered 429 `rate_limited` — caught here, reported via `onBlocked`
 * so the parent can stop saying «Мы отправили код». Any OTHER rejection is
 * left to propagate exactly as before.
 */
export function OtpResendButton({
  onResend,
  cooldownSeconds = DEFAULT_COOLDOWN_S,
  blockedUntil = null,
  onBlocked,
}: {
  onResend: () => Promise<void>;
  cooldownSeconds?: number;
  /** Epoch ms until which no code can be requested (server refusal). */
  blockedUntil?: number | null;
  /** A resend press was refused for being over the cap; `until` is epoch ms. */
  onBlocked?: (until: number) => void;
}) {
  const t = useT();
  // A refusal already in force REPLACES the cooldown — no code was just sent,
  // so there is nothing to give a head start; when it lifts, the button is live.
  const [remaining, setRemaining] = useState(() =>
    blockedUntil !== null && blockedUntil > Date.now() ? 0 : cooldownSeconds,
  );
  const [busy, setBusy] = useState(false);
  /** A refusal this button caught itself (the parent may not be listening). */
  const [ownBlockedUntil, setOwnBlockedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const until = Math.max(blockedUntil ?? 0, ownBlockedUntil ?? 0);
  const blocked = until > now;

  useEffect(() => {
    if (remaining <= 0) return;
    const id = window.setTimeout(() => setRemaining((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [remaining]);

  // While refused, re-read the clock so «N мин.» counts down and the button
  // returns on its own the moment the window opens.
  useEffect(() => {
    if (!blocked) return;
    const id = window.setTimeout(() => setNow(Date.now()), 1000);
    return () => window.clearTimeout(id);
  }, [blocked, now]);

  // A refusal handed down AFTER mount (the parent caught it somewhere else)
  // starts the clock too; `now` is otherwise stale until the next tick.
  useEffect(() => {
    if (blockedUntil !== null) setNow(Date.now());
  }, [blockedUntil]);

  if (blocked) {
    return (
      <p
        role="status"
        className="rounded-lg border border-edge bg-bone p-3 text-center text-[13px] leading-snug text-ink"
      >
        {t(OTP_RATE_LIMIT_KEY, { minutes: otpWaitMinutes((until - now) / 1000) })}
      </p>
    );
  }

  // No spam line here: the «check Spam» reassurance lives once already — in the
  // code-sent hint under the field (or the iCloud note), so repeating it under the
  // resend would be the third copy on one screen.
  if (remaining > 0) {
    return (
      <p className="text-center text-[12.5px] text-ink-muted">
        {t('auth.otp.resendIn', { seconds: remaining })}
      </p>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-edge bg-bone p-3 text-center">
      <p className="text-[13px] font-semibold text-ink">{t('auth.otp.notArrived')}</p>
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void onResend()
            .then(
              () => setRemaining(cooldownSeconds),
              (err: unknown) => {
                const sec = otpRateLimitSec(err);
                if (sec === null) throw err;
                const next = Date.now() + sec * 1000;
                setOwnBlockedUntil(next);
                setNow(Date.now());
                onBlocked?.(next);
              },
            )
            .finally(() => setBusy(false));
        }}
        className="h-9 w-full rounded-md border border-brand bg-bone-surface text-[13.5px] font-bold text-brand transition-colors hover:bg-brand-dim disabled:opacity-60"
      >
        {t('auth.otp.resend')}
      </button>
    </div>
  );
}
